/**
 * Yeati-loc service - talks to the Core::Catalog admin API and adapts
 * responses/payloads to the legacy yeati-loc UI types.
 *
 * NOTE: Some flows are still partially mocked because the backend doesn't
 * expose the corresponding endpoint yet:
 *   - Dashboard KPIs are computed client-side from list endpoints.
 *   - The flat "categories" list returns the legacy hardcoded taxonomy
 *     (the backend has user-defined per-collection trees instead).
 *
 * createBooking: mirror du flux `POST /api/admin/ecom_orders/orders`
 * côté bons plans. Le CSE crée un booking au nom d'un salarié - le
 * backend traverse la state machine jusqu'à `confirmed` (pas de
 * paiement, c'est un pay-later que l'admin peut solder via
 * `settle_with_payment`).
 */
import axiosInstance from "@/axiosConfig";
import type { HistoryResponse } from "@/shared/components/AuditHistory/auditHistory.types";
import type { AccountingLinesResponse } from "@/shared/types/accountingLineDetail.types";
import type { JobServiceResponse } from "@/shared/types/job.types";
import { getApiUrl } from "@/shared/utils/api";
import { DEFAULT_CATEGORIES } from "../configs/constants";
import { YEATI_LOC_ENDPOINTS } from "../configs/endpoints";
import { createdAtRansackBounds } from "../utils/bookingFilters";
import { buildVoyageGridPayload } from "../utils/voyageGrid";
import {
  adaptBookingResponse,
  adaptItemToProduct,
  adaptProductCreatePayload,
  adaptProductUpdatePayload,
} from "./catalogAdapter";
import type {
  BatchDeleteProductsResponse,
  BookingBatchSelectionPayload,
  BookingBatchTransitionResponse,
  BulkImportListResponse,
  BulkImportResponse,
  BulkImportShowResponse,
  CatalogBookingsListResponse,
  CatalogBookingShowResponse,
  CatalogCategoriesListResponse,
  CatalogCategoryResponse,
  CatalogCollectionCreatePayload,
  CatalogCollectionsListResponse,
  CatalogCollectionDuplicateResponse,
  CatalogCollectionResponse,
  CatalogCollectionShowResponse,
  CatalogCollectionUpdatePayload,
  CatalogItemCreatePayload,
  CatalogItemsListResponse,
  CatalogItemDuplicateResponse,
  CatalogItemShowResponse,
  CatalogItemUpdatePayload,
  CatalogBookingVoyagePayload,
  CatalogFormTemplatePayload,
  CatalogFormTemplateResponse,
  CatalogUnavailabilityResponse,
  IsbndbBookResponse,
  IsbndbBookCoverResponse,
  IsbndbBooksSearchResponse,
} from "../types/catalog.api.types";
import type {
  BookingStatus,
  CatalogApplySubsidyItem,
  CatalogSettings,
  CatalogSubsidyEvaluatePayload,
  CatalogSubsidyEvaluationResponse,
  CreateBookingPayload,
  CreateProductPayload,
  DashboardKpi,
  GetBookingsParams,
  GetProductsParams,
  LedgerEntriesResponse,
  RentalBooking,
  RentalCategory,
  RentalProduct,
  RentalUnavailability,
  UnavailabilityReason,
  UpdateProductPayload,
  VoyageGrid,
} from "../types/yeatiLoc.types";
import type { BookingBatchTransition } from "../utils/bookingRowActions";
import type { FormTemplate } from "../utils/formTemplates";

const url = (path: string) => getApiUrl(path);

const adaptFormTemplate = (template: CatalogFormTemplateResponse): FormTemplate => ({
  id: template.id,
  name: template.name,
  description: template.description ?? null,
  form_fields: [...template.form_fields]
    .sort((a, b) => a.rank - b.rank)
    .map((field) => ({
      name: field.name,
      label: field.label,
      field_type: field.field_type,
      required: field.required,
      options: field.options ?? {},
    })),
});

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function fetchDefaultCollectionId(): Promise<string> {
  const res = await axiosInstance.get<CatalogCollectionsListResponse>(
    url(YEATI_LOC_ENDPOINTS.collections.LIST),
  );
  const first = res.data.collections[0];
  if (!first) {
    throw new Error(
      "Aucune collection trouvée. Créez d'abord une collection avant d'ajouter un produit.",
    );
  }
  return first.id;
}

function buildProductsListParams(
  params?: GetProductsParams,
): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const q: Record<string, string | number | boolean> = {};
  if (params.search) q["q[name_cont]"] = params.search;
  if (params.status === "archived") q["q[active_eq]"] = false;
  else if (params.status === "published") q["q[active_eq]"] = true;
  if (params.category_id) q["q[catalog_collection_id_eq]"] = params.category_id;
  if (params.kind) q["q[kind_eq]"] = params.kind;
  if (params.per_page) q.per_page = params.per_page;
  if (params.page) q.page = params.page;
  return Object.keys(q).length ? q : undefined;
}

// Le vocabulaire UI est plus compact que celui de l'API (statuts
// unifiés 31/08) : chaque statut d'écran couvre un ou plusieurs
// statuts de base.
const UI_STATUS_TO_API: Record<string, string[]> = {
  cart: ["open"],
  pending_checkout: ["pending_checkout"],
  pending: ["pending_approval"],
  approved: ["approved"],
  confirmed: ["confirmed"],
  in_progress: ["in_progress"],
  pending_return: ["return_pending"],
  completed: ["return_completed"],
  cancelled: ["cancelled"],
  rejected: ["rejected"],
};

/** Mêmes filtres que la liste, sous la forme d'un objet `q` pour un corps
 * JSON (`{ status_in: [...], smart_search: "..." }`), sans le tri. */
export function buildBookingsRansack(params?: GetBookingsParams): Record<string, unknown> {
  const flat = buildBookingsListQuery(params);
  const q: Record<string, unknown> = {};
  Object.entries(flat).forEach(([key, value]) => {
    const match = /^q\[([^\]]+)\](\[\])?$/.exec(key);
    if (!match || match[1] === "s") return;
    q[match[1]] = value;
  });
  return q;
}

export function buildBookingsListQuery(
  params?: GetBookingsParams,
): Record<string, string | string[]> {
  // Ransack supporte les filtres multi-valeur via `q[xxx_in][]=` ;
  // axios sérialise les tableaux dans la query string avec la
  // convention `?q[status_in][]=a&q[status_in][]=b`.
  const q: Record<string, string | string[]> = {};

  if (params?.statuses && params.statuses.length > 0) {
    q["q[status_in][]"] = params.statuses.flatMap((s) => UI_STATUS_TO_API[s] ?? [s]);
  }
  if (params?.product_ids && params.product_ids.length > 0) {
    q["q[catalog_item_id_in][]"] = params.product_ids;
  }
  if (params?.search?.trim()) {
    // Ransackable scope `smart_search` côté Booking : recherche
    // case-insensitive ET accent-insensitive (PG `unaccent`) sur
    // `reference` (ID booking CAT-…), prénom/nom salarié, et nom
    // du produit. `Bohémond` matche `bohemond`, `André` matche
    // `andre`, etc.
    q["q[smart_search]"] = params.search.trim();
  }
  if (params?.date_from) q["q[start_date_gteq]"] = params.date_from;
  if (params?.date_to) q["q[end_date_lteq]"] = params.date_to;
  Object.assign(q, createdAtRansackBounds(params?.created_from, params?.created_to));
  // Filtre via l'association `item` (whitelistée côté Booking) sur le
  // `kind` de l'item - `q[item_kind_eq]=livre` pour le tableau de bord
  // des livres non restitués (ADM-638).
  if (params?.item_kind) q["q[item_kind_eq]"] = params.item_kind;
  // Filtre via l'association `item` sur sa collection - scope la page
  // Réservations à une collection précise (sidebar « Réservations <X> »).
  if (params?.item_collection_id) q["q[item_catalog_collection_id_eq]"] = params.item_collection_id;
  if (params?.employee_ids && params.employee_ids.length > 0) q["q[employee_id_in][]"] = params.employee_ids;
  // Montants saisis en euros, filtrés en centimes côté API.
  const cents = (euros: number | undefined) => (euros === undefined ? undefined : String(Math.round(euros * 100)));
  const amountBounds: [string, number | undefined][] = [
    ["q[total_price_cents_gteq]", params?.total_min],
    ["q[total_price_cents_lteq]", params?.total_max],
    ["q[subsidy_amount_cents_gteq]", params?.subsidy_min],
    ["q[subsidy_amount_cents_lteq]", params?.subsidy_max],
  ];
  amountBounds.forEach(([key, value]) => {
    const v = cents(value);
    if (v !== undefined) q[key] = v;
  });
  // Tri par défaut : réservation la plus récemment créée en haut
  // (created_at desc) — reflète l'ordre d'arrivée des demandes.
  q["q[s]"] = "created_at desc";

  return q;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class YeatiLocService {
  static async getProducts(params?: GetProductsParams): Promise<RentalProduct[]> {
    const res = await axiosInstance.get<CatalogItemsListResponse>(
      url(YEATI_LOC_ENDPOINTS.products.LIST),
      { params: buildProductsListParams(params) },
    );
    return res.data.items.map(adaptItemToProduct);
  }

  static async getProductsPage(
    params: GetProductsParams & { page: number },
  ): Promise<{ items: RentalProduct[], metadata: { total_items: number, current_page: number, total_pages: number } }> {
    const res = await axiosInstance.get<CatalogItemsListResponse>(
      url(YEATI_LOC_ENDPOINTS.products.LIST),
      { params: { ...buildProductsListParams(params), page: params.page } },
    );
    return {
      items: res.data.items.map(adaptItemToProduct),
      metadata: res.data.metadata,
    };
  }

  /** `employeeId` : l'élu réserve pour ce salarié ; la grille d'un voyage
   * porte alors sa tranche d'après son profil. */
  static async getProduct(id: string, employeeId?: string): Promise<RentalProduct | null> {
    const res = await axiosInstance.get<CatalogItemShowResponse>(
      url(YEATI_LOC_ENDPOINTS.products.SHOW(id)),
      employeeId ? { params: { employee_id: employeeId } } : undefined,
    );
    return adaptItemToProduct(res.data.item);
  }

  static async createProduct(
    payload: CreateProductPayload,
  ): Promise<RentalProduct> {
    // Prefer the collection the wizard explicitly picked; fall back to the
    // first active one when the caller hasn't surfaced the choice.
    const collectionId = payload.collection_id ?? (await fetchDefaultCollectionId());
    const apiPayload: CatalogItemCreatePayload = adaptProductCreatePayload(
      payload,
      { catalogCollectionId: collectionId },
    );
    const res = await axiosInstance.post<CatalogItemShowResponse>(
      url(YEATI_LOC_ENDPOINTS.products.CREATE),
      apiPayload,
    );
    return adaptItemToProduct(res.data.item);
  }

  static async updateProduct(
    id: string,
    payload: UpdateProductPayload,
    originalProduct?: RentalProduct,
  ): Promise<RentalProduct> {
    const apiPayload: CatalogItemUpdatePayload = adaptProductUpdatePayload(payload, {
      originalPricing: originalProduct?.pricing,
      originalFormFields: originalProduct?.form_fields,
      originalSlots: originalProduct?.slots,
      originalVariants: originalProduct?.variants,
    });
    const res = await axiosInstance.patch<CatalogItemShowResponse>(
      url(YEATI_LOC_ENDPOINTS.products.UPDATE(id)),
      apiPayload,
    );
    return adaptItemToProduct(res.data.item);
  }

  /** Voyage : enregistre toute la grille de prix (remplacement complet,
   * transactionnel côté API). Renvoie l'article à jour. */
  static async updateVoyageGrid(id: string, grid: VoyageGrid): Promise<RentalProduct> {
    const res = await axiosInstance.put<CatalogItemShowResponse>(
      url(YEATI_LOC_ENDPOINTS.products.VOYAGE_GRID(id)),
      buildVoyageGridPayload(grid),
    );
    return adaptItemToProduct(res.data.item);
  }

  /** Copie l'article en brouillon, nommée « <nom> (copie) ». `warnings` :
   * ce que l'élu doit vérifier sur la copie. */
  static async duplicateProduct(id: string): Promise<{ product: RentalProduct, warnings: string[] }> {
    const res = await axiosInstance.post<CatalogItemDuplicateResponse>(
      url(YEATI_LOC_ENDPOINTS.products.DUPLICATE(id)),
    );
    return { product: adaptItemToProduct(res.data.item), warnings: res.data.warnings ?? [] };
  }

  static async deleteProduct(id: string): Promise<void> {
    await axiosInstance.delete(url(YEATI_LOC_ENDPOINTS.products.DELETE(id)));
  }

  static async batchDeleteProducts(
    ids: string[],
  ): Promise<BatchDeleteProductsResponse> {
    const res = await axiosInstance.delete<BatchDeleteProductsResponse>(
      url(YEATI_LOC_ENDPOINTS.products.BATCH_DELETE),
      { data: { item_ids: ids } },
    );
    return res.data;
  }

  // -------------------------------------------------------------------------
  // Bookings (admin: list + show + transitions)
  // -------------------------------------------------------------------------

  static async getBookings(params?: GetBookingsParams): Promise<RentalBooking[]> {
    const q = buildBookingsListQuery(params);
    const res = await axiosInstance.get<CatalogBookingsListResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.LIST),
      { params: q },
    );
    return res.data.bookings.map((b) => adaptBookingResponse(b));
  }

  /** Variante paginée pour le scroll infini de la page Réservations.
   * Le backend pagine à 50 par défaut ; sans `page`/`per_page` on ne
   * voyait que la première page. Renvoie les bookings + la metadata
   * de pagination pour piloter `getNextPageParam`. */
  static async getBookingsPage(
    params: GetBookingsParams & { page: number, per_page: number },
  ): Promise<{ bookings: RentalBooking[], metadata: CatalogBookingsListResponse["metadata"] }> {
    const { page, per_page, ...filters } = params;
    const q: Record<string, string | string[] | number> = {
      ...buildBookingsListQuery(filters),
      page,
      per_page,
    };
    const res = await axiosInstance.get<CatalogBookingsListResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.LIST),
      { params: q },
    );
    return {
      bookings: res.data.bookings.map((b) => adaptBookingResponse(b)),
      metadata: res.data.metadata,
    };
  }

  /** Export Excel des demandes : mêmes filtres que la liste, sans
   * pagination. L'API lance un job (`CATALOG BOOKING EXPORT XLSX`) dont la
   * modale partagée suit l'avancement et propose le fichier. */
  static async exportBookingsXlsx(params?: GetBookingsParams): Promise<JobServiceResponse> {
    const res = await axiosInstance.get<JobServiceResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.EXPORT_XLSX),
      { params: buildBookingsListQuery(params) },
    );
    return res.data;
  }

  /** Même transition sur plusieurs demandes (ids, ou toutes les demandes
   * des filtres). Chaque demande est traitée à part côté API : un échec
   * n'empêche pas les autres. */
  static async batchTransitionBookings(
    selection: string[] | BookingBatchSelectionPayload,
    transition: BookingBatchTransition,
  ): Promise<BookingBatchTransitionResponse> {
    const payload = Array.isArray(selection) ? { booking_ids: selection } : selection;
    const res = await axiosInstance.post<BookingBatchTransitionResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.BATCH_TRANSITION),
      { ...payload, transition },
    );
    return res.data;
  }

  static async getBooking(id: string): Promise<RentalBooking | null> {
    const res = await axiosInstance.get<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.SHOW(id)),
    );
    return adaptBookingResponse(res.data.booking);
  }

  /** Arbre policies V2 + entrées ledger pour un booking. Mirror du
   * pattern ecom-orders / refunds (réponse strictement identique côté
   * backend - voir `Catalog::Bookings::LedgerEntriesController`). */
  static async getBookingLedgerEntries(
    bookingId: string,
    params?: { holder_type?: string, holder_id?: string },
  ): Promise<LedgerEntriesResponse> {
    const { data } = await axiosInstance.get<LedgerEntriesResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.LEDGER_ENTRIES(bookingId)),
      { params },
    );
    return data;
  }

  static async createBooking(payload: CreateBookingPayload): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.CREATE),
      { booking: payload },
    );
    return adaptBookingResponse(res.data.booking);
  }

  /** Supprime une demande de location annulée/refusée (ADM-834). Le
   * backend garde-fou le statut + l'absence de trace financière. */
  static async deleteBooking(id: string): Promise<void> {
    await axiosInstance.delete(url(YEATI_LOC_ENDPOINTS.bookings.DELETE(id)));
  }

  /** Timeline d'audit des actions admin sur une réservation (drawer
   * Historique). Réponse `{ events, reference }` iso ecom-orders / refunds. */
  static async getBookingHistory(bookingId: string): Promise<HistoryResponse> {
    const { data } = await axiosInstance.get<HistoryResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.HISTORY(bookingId)),
    );
    return data;
  }

  /**
   * Maps the legacy UI `updateBookingStatus(id, status)` call to the
   * appropriate AASM action endpoint on the backend.
   */
  static async updateBookingStatus(
    id: string,
    status: BookingStatus,
  ): Promise<RentalBooking> {
    let path: string;
    switch (status) {
      case "confirmed":
        path = YEATI_LOC_ENDPOINTS.bookings.APPROVE(id);
        break;
      case "in_progress":
        path = YEATI_LOC_ENDPOINTS.bookings.START(id);
        break;
      case "completed":
        path = YEATI_LOC_ENDPOINTS.bookings.REQUEST_RETURN(id);
        break;
      case "cancelled":
        path = YEATI_LOC_ENDPOINTS.bookings.CANCEL(id);
        break;
      case "pending":
      default:
        return (await YeatiLocService.getBooking(id)) as RentalBooking;
    }
    const res = await axiosInstance.post<CatalogBookingShowResponse>(url(path));
    return adaptBookingResponse(res.data.booking);
  }

  /**
   * Reject a pending_approval booking. Distinct from cancel - the
   * underlying AASM event is `reject`, which transitions to the
   * `rejected` terminal state and triggers the rejection notification
   * email to the employee.
   */
  /**
   * Valide le panier posé par l'élu : la réservation en sort, les dates
   * sont immobilisées et l'écriture comptable est posée avec la
   * subvention finalement retenue.
   */
  static async checkoutBooking(id: string): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.CHECKOUT(id)),
    );
    return adaptBookingResponse(res.data.booking);
  }

  /** Voyage : l'élu corrige la tranche retenue et / ou la catégorie de
   * participants avant validation. L'API recalcule lignes, total, places
   * et subvention proposée. */
  static async correctBookingVoyage(
    id: string,
    voyage: CatalogBookingVoyagePayload["voyage"],
  ): Promise<RentalBooking> {
    const res = await axiosInstance.patch<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.VOYAGE(id)),
      { voyage } satisfies CatalogBookingVoyagePayload,
    );
    return adaptBookingResponse(res.data.booking);
  }

  static async rejectBooking(id: string, comment?: string): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.REJECT(id)),
      comment ? { comment } : undefined,
    );
    return adaptBookingResponse(res.data.booking);
  }

  /**
   * Prolonge la `end_date` d'un booking. Le backend revérifie la
   * disponibilité sur le créneau ajouté + la durée max effective.
   */
  static async extendBooking(id: string, newEndDate: string): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.EXTEND_LOAN(id)),
      { new_end_date: newEndDate },
    );
    return adaptBookingResponse(res.data.booking);
  }

  /**
   * Issue a refund for a paid booking. Triggers the Paybox refund and
   * reverses the V2 subsidy ledger entries server-side. The backend is
   * idempotent - calling refund on an already-refunded booking returns
   * 422 with an explicit message.
   */
  static async refundBooking(id: string, comment?: string): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.REFUND(id)),
      comment ? { comment } : undefined,
    );
    return adaptBookingResponse(res.data.booking);
  }

  /** Applique la subvention V2 avec des montants retenus par holder
   * (multi-holder iso bons plans). Les montants sont en euros (float)
   * et capés au max éligible Pipeline côté backend. */
  static async applyBookingSubsidy(
    id: string,
    items: CatalogApplySubsidyItem[],
  ): Promise<RentalBooking> {
    const res = await axiosInstance.patch<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.APPLY_SUBSIDY(id)),
      { subsidy_items: items },
    );
    return adaptBookingResponse(res.data.booking);
  }

  /** Évalue la subvention V2 sur une résa pour chaque holder candidat
   * (salarié + ayants droits participants). Read-only - ne persiste
   * rien. Le front s'en sert pour afficher les inputs « Montant à
   * retenir » avec le max éligible Pipeline par holder. */
  static async evaluateCatalogSubsidies(
    payload: CatalogSubsidyEvaluatePayload,
  ): Promise<CatalogSubsidyEvaluationResponse> {
    const res = await axiosInstance.post<CatalogSubsidyEvaluationResponse>(
      url(YEATI_LOC_ENDPOINTS.subsidy.CATALOG_EVALUATIONS),
      payload,
    );
    return res.data;
  }

  /**
   * Compensation cross-AO. `amounts` = { ao_id (en string) → montant signé }.
   * La somme doit être nulle. Crée un Settlement « Compensation » + une
   * CMA par AO côté API.
   */
  static async compensateBooking(
    id: string,
    amounts: Record<string, number>,
  ): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.COMPENSATE(id)),
      { amounts },
    );
    return adaptBookingResponse(res.data.booking);
  }

  /**
   * Crée des CMAs `type_budget='STOCK'` pour matérialiser une remise
   * (sortie matériel) ou une reprise (retour) sur les AOs catalog du
   * booking. Mirror du `Core::Activity::Order#deliver` côté
   * ecom-orders. Le backend peut auto-déclencher l'AASM `start!` ou
   * `request_return!` quand le stock est totalement remis / repris.
   * Renvoie les `accounting_lines` mises à jour comme ecom-orders
   * pour que le tab Stock puisse refresh sans refetch booking.
   */
  static async deliverBookingStock(payload: {
    bookingId: string
    /** Absent pour une réservation gratuite (DEC-45) : elle ne porte
     * aucune écriture, la remise / reprise vit sur ses compteurs. */
    operationIds?: number[]
    direction?: "remise" | "reprise"
    quantities?: Record<string, number>
    /** Quantité partielle du chemin sans écriture. */
    quantity?: number
  }): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.post<AccountingLinesResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.DELIVER(payload.bookingId)),
      payload.operationIds?.length
        ? {
          operation_ids: payload.operationIds,
          direction: payload.direction ?? "remise",
          quantities: payload.quantities,
        }
        : {
          direction: payload.direction ?? "remise",
          quantity: payload.quantity,
        },
    );
    return data;
  }

  /**
   * Crée un constat de retour pour un booking en `pending_return`.
   * Côté API, `Core::Catalog::ReturnManager` enregistre l'état du
   * matériel (good/damaged/lost), saisit l'éventuelle pénalité (en
   * cents) et fait transitionner la booking de `pending_return` à
   * `completed`. Si une pénalité > 0 est saisie, une AO de pénalité
   * est créée par-dessus pour solder la caution / facturer la casse.
   */
  static async createReturnRecord(payload: {
    bookingId: string
    condition: "good" | "damaged" | "lost"
    notes?: string
    penaltyAmountCents?: number
  }): Promise<RentalBooking> {
    await axiosInstance.post(
      url(YEATI_LOC_ENDPOINTS.returnRecords.CREATE),
      {
        return_record: {
          catalog_booking_id: payload.bookingId,
          condition: payload.condition,
          notes: payload.notes,
          penalty_amount_cents: payload.penaltyAmountCents ?? 0,
        },
      },
    );
    // Le backend renvoie la return_record, pas le booking. On re-fetch
    // pour avoir l'état AASM le plus à jour (status=completed,
    // return_record peuplé, AOs si pénalité).
    return (await YeatiLocService.getBooking(payload.bookingId)) as RentalBooking;
  }

  /**
   * Affecte les AOs choisies à un mouvement bancaire OU un règlement
   * existant. `movementId` ou `settlementId` (prefix_id) requis.
   */
  static async settleBookingWithPayment(
    id: string,
    payload: {
      operationIds: number[]
      movementId?: string
      settlementId?: string
    },
  ): Promise<RentalBooking> {
    const res = await axiosInstance.post<CatalogBookingShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.SETTLE_WITH_PAYMENT(id)),
      {
        operation_ids: payload.operationIds,
        movement_id: payload.movementId,
        settlement_id: payload.settlementId,
      },
    );
    return adaptBookingResponse(res.data.booking);
  }

  // -------------------------------------------------------------------------
  // Accounting (iso ecom-orders / refunds)
  // -------------------------------------------------------------------------

  /**
   * Returns ALL accounting lines (Activity Operations) for the booking's
   * employee - cross-modules : catalog, bons plans, refunds. Same shape as
   * `EcomOrdersService.getAccountingLines` so the shared `AccountingTable`
   * can render them.
   */
  static async getAccountingLines(
    bookingId: string,
    includeSettled = false,
  ): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.get<AccountingLinesResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.ACCOUNTING_LINES(bookingId)),
      { params: { include_settled: includeSettled } },
    );
    return data;
  }

  /**
   * Iso `EcomOrdersService.compensateOperations`. Backend accepts:
   * - `operation_ids` only → compensation totale auto-remplie depuis
   *   `solde_affectation`
   * - `amounts` (map ao_id → signed amount, somme = 0) → compensation
   *   partielle pilotée par le front
   * Le backend retourne la réponse `accounting_lines` mise à jour.
   */
  static async compensateOperations(
    bookingId: string,
    operationIds: string[],
    amounts?: Record<string, number>,
  ): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.post<AccountingLinesResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.COMPENSATE(bookingId)),
      { operation_ids: operationIds, amounts },
    );
    return data;
  }

  /**
   * Iso `EcomOrdersService.settleWithPayment`. Affecte les AOs choisies
   * à un mouvement bancaire OU à un règlement existant. Renvoie les
   * `accounting_lines` mises à jour.
   */
  static async settleWithPayment(
    bookingId: string,
    payload: {
      operation_ids: string[]
      settlement_id?: string | null
      movement_id?: string | null
    },
  ): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.post<AccountingLinesResponse>(
      url(YEATI_LOC_ENDPOINTS.bookings.SETTLE_WITH_PAYMENT(bookingId)),
      payload,
    );
    return data;
  }

  // -------------------------------------------------------------------------
  // Categories (legacy hardcoded taxonomy - backend uses per-collection trees)
  // -------------------------------------------------------------------------

  static async getCategories(): Promise<RentalCategory[]> {
    return Promise.resolve(DEFAULT_CATEGORIES);
  }

  // -------------------------------------------------------------------------
  // Dashboard KPIs - computed client-side from list endpoints
  // -------------------------------------------------------------------------

  static async getDashboardKpis(): Promise<DashboardKpi[]> {
    const [products, bookings] = await Promise.all([
      YeatiLocService.getProducts(),
      YeatiLocService.getBookings(),
    ]);
    const activeProducts = products.filter((p) => p.status === "published");
    const activeBookings = bookings.filter(
      // `approved` compte comme active : la résa est acquise (validée
      // par l'élu), seul le règlement du bénéficiaire manque.
      (b) => b.status === "confirmed" || b.status === "in_progress" || b.status === "approved",
    );
    const pendingBookings = bookings.filter((b) => b.status === "pending");
    const totalRevenue = bookings.reduce(
      (sum, b) => sum + (b.total_price ?? 0),
      0,
    );

    return [
      {
        label: "Produits actifs",
        value: activeProducts.length,
        icon: "Package",
        color: "#3b82f6",
      },
      {
        label: "Réservations actives",
        value: activeBookings.length,
        icon: "CalendarCheck",
        color: "#8b5cf6",
      },
      {
        label: "Revenus cumulés",
        value: `${totalRevenue.toFixed(0)} EUR`,
        icon: "Euro",
        color: "#10b981",
      },
      {
        label: "En attente",
        value: pendingBookings.length,
        icon: "Clock",
        color: "#f59e0b",
      },
    ];
  }

  static async getRecentBookings(limit = 5): Promise<RentalBooking[]> {
    const all = await YeatiLocService.getBookings();
    return all
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .slice(0, limit);
  }

  // -------------------------------------------------------------------------
  // Collections (raw - useful for screens that need to surface them)
  // -------------------------------------------------------------------------

  static async getCollections() {
    const res = await axiosInstance.get<CatalogCollectionsListResponse>(
      url(YEATI_LOC_ENDPOINTS.collections.LIST),
    );
    return res.data.collections;
  }

  static async getCollection(id: string) {
    const res = await axiosInstance.get<CatalogCollectionShowResponse>(
      url(YEATI_LOC_ENDPOINTS.collections.SHOW(id)),
    );
    return res.data.collection;
  }

  static async createCollection(payload: CatalogCollectionCreatePayload) {
    const res = await axiosInstance.post<CatalogCollectionShowResponse>(
      url(YEATI_LOC_ENDPOINTS.collections.CREATE),
      payload,
    );
    return res.data.collection;
  }

  static async updateCollection(
    id: string,
    payload: CatalogCollectionUpdatePayload,
  ) {
    const res = await axiosInstance.patch<CatalogCollectionShowResponse>(
      url(YEATI_LOC_ENDPOINTS.collections.UPDATE(id)),
      payload,
    );
    return res.data.collection;
  }

  static async deleteCollection(id: string) {
    await axiosInstance.delete(url(YEATI_LOC_ENDPOINTS.collections.DELETE(id)));
  }

  /** Copie la collection (inactive, « <nom> (copie) ») avec ses catégories,
   * ses quotas et ses produits non archivés, en brouillon. */
  static async duplicateCollection(
    id: string,
  ): Promise<{ collection: CatalogCollectionResponse, warnings: string[] }> {
    const res = await axiosInstance.post<CatalogCollectionDuplicateResponse>(
      url(YEATI_LOC_ENDPOINTS.collections.DUPLICATE(id)),
    );
    return { collection: res.data.collection, warnings: res.data.warnings ?? [] };
  }

  // Catégories taxonomiques d'une collection (peuplées soit
  // manuellement, soit auto-créées par l'import CSV livres).
  static async getCatalogCategories(collectionId: string): Promise<CatalogCategoryResponse[]> {
    const res = await axiosInstance.get<CatalogCategoriesListResponse>(
      url(YEATI_LOC_ENDPOINTS.categories.LIST_FOR_COLLECTION(collectionId)),
    );
    return res.data.categories;
  }

  static async createCatalogCategory(
    collectionId: string,
    payload: { name: string, parent_id?: string | null },
  ): Promise<CatalogCategoryResponse> {
    const res = await axiosInstance.post<{ category: CatalogCategoryResponse }>(
      url(YEATI_LOC_ENDPOINTS.categories.CREATE_FOR_COLLECTION(collectionId)),
      { category: payload },
    );
    return res.data.category;
  }

  static async updateCatalogCategory(
    id: string,
    payload: { name?: string, parent_id?: string | null },
  ): Promise<CatalogCategoryResponse> {
    const res = await axiosInstance.patch<{ category: CatalogCategoryResponse }>(
      url(YEATI_LOC_ENDPOINTS.categories.UPDATE(id)),
      { category: payload },
    );
    return res.data.category;
  }

  static async deleteCatalogCategory(id: string): Promise<void> {
    await axiosInstance.delete(url(YEATI_LOC_ENDPOINTS.categories.DELETE(id)));
  }

  // ISBNdb - proxy serveur (clé API stockée backend, anti-CORS
  // pour la couverture, voir api/admin/catalog/isbndb_controller).
  static async searchIsbndb(query: string): Promise<IsbndbBookResponse[]> {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const res = await axiosInstance.get<IsbndbBooksSearchResponse>(
      url(YEATI_LOC_ENDPOINTS.isbndb.SEARCH),
      { params: { q: trimmed, max_results: 10 } },
    );
    return res.data.books;
  }

  static async importIsbndbCover(thumbnailUrl: string): Promise<IsbndbBookCoverResponse> {
    const res = await axiosInstance.post<IsbndbBookCoverResponse>(
      url(YEATI_LOC_ENDPOINTS.isbndb.IMPORT_COVER),
      { url: thumbnailUrl },
    );
    return res.data;
  }

  // Bulk import livres (médiathèque CSE) - upload CSV puis polling
  // via `getBulkImport(id)` jusqu'au statut `done` / `failed`.
  static async createBulkImport(
    payload: { file: File, collectionId: string },
  ): Promise<BulkImportResponse> {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("catalog_collection_id", payload.collectionId);
    const res = await axiosInstance.post<BulkImportShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bulkImports.CREATE),
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data.bulk_import;
  }

  static async getBulkImport(id: string): Promise<BulkImportResponse> {
    const res = await axiosInstance.get<BulkImportShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bulkImports.SHOW(id)),
    );
    return res.data.bulk_import;
  }

  static async getActiveBulkImports(): Promise<BulkImportResponse[]> {
    const res = await axiosInstance.get<BulkImportListResponse>(
      url(YEATI_LOC_ENDPOINTS.bulkImports.LIST),
      { params: { active: "true" } },
    );
    return res.data.bulk_imports;
  }

  static async resumeBulkImport(id: string): Promise<BulkImportResponse> {
    const res = await axiosInstance.post<BulkImportShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bulkImports.RESUME(id)),
    );
    return res.data.bulk_import;
  }

  /**
   * Marque un import en cours (typiquement `paused_on_quota` qui traîne)
   * comme `failed` côté backend pour le faire disparaître de la liste
   * des imports actifs et du banner.
   */
  static async cancelBulkImport(id: string): Promise<BulkImportResponse> {
    const res = await axiosInstance.post<BulkImportShowResponse>(
      url(YEATI_LOC_ENDPOINTS.bulkImports.CANCEL(id)),
    );
    return res.data.bulk_import;
  }

  // Settings org-level - liste maître des équipements hébergement
  // (+ futurs settings). Lecture publique pour tout rep ; écriture
  // gated `manage_access?` côté policy.
  static async getCatalogSettings(): Promise<CatalogSettings> {
    const res = await axiosInstance.get<{ catalog_settings: CatalogSettings }>(
      url(YEATI_LOC_ENDPOINTS.settings.SHOW),
    );
    return res.data.catalog_settings;
  }

  static async updateCatalogSettings(payload: Partial<CatalogSettings>): Promise<CatalogSettings> {
    const res = await axiosInstance.patch<{ catalog_settings: CatalogSettings }>(
      url(YEATI_LOC_ENDPOINTS.settings.UPDATE),
      { catalog_settings: payload },
    );
    return res.data.catalog_settings;
  }

  // ============ FORM TEMPLATES ============
  // Modèles de formulaire de réservation de l'organisation. Un modèle se
  // recopie dans le formulaire d'un produit : aucun lien ne subsiste.
  static async listFormTemplates(): Promise<FormTemplate[]> {
    const res = await axiosInstance.get<{ form_templates: CatalogFormTemplateResponse[] }>(
      url(YEATI_LOC_ENDPOINTS.formTemplates.LIST),
      { params: { per_page: 100 } },
    );
    return res.data.form_templates.map(adaptFormTemplate);
  }

  static async createFormTemplate(payload: CatalogFormTemplatePayload): Promise<FormTemplate> {
    const res = await axiosInstance.post<{ form_template: CatalogFormTemplateResponse }>(
      url(YEATI_LOC_ENDPOINTS.formTemplates.CREATE),
      payload,
    );
    return adaptFormTemplate(res.data.form_template);
  }

  static async renameFormTemplate(id: string, name: string): Promise<FormTemplate> {
    const res = await axiosInstance.patch<{ form_template: CatalogFormTemplateResponse }>(
      url(YEATI_LOC_ENDPOINTS.formTemplates.UPDATE(id)),
      { form_template: { name } },
    );
    return adaptFormTemplate(res.data.form_template);
  }

  static async deleteFormTemplate(id: string): Promise<void> {
    await axiosInstance.delete(url(YEATI_LOC_ENDPOINTS.formTemplates.DELETE(id)));
  }

  // ============ UNAVAILABILITIES ============
  // Indispos manuelles posées sur les items `stock_strategy: calendar`.
  // Le backend expose aussi les `booked` (auto-générées) - on les
  // affiche en lecture seule côté admin pour matérialiser la cause.
  static async listUnavailabilities(itemId: string): Promise<RentalUnavailability[]> {
    const res = await axiosInstance.get<{ unavailabilities: CatalogUnavailabilityResponse[] }>(
      url(YEATI_LOC_ENDPOINTS.unavailabilities.LIST_FOR_ITEM(itemId)),
    );
    return res.data.unavailabilities.map((u) => ({
      id: u.id,
      start_date: u.start_date,
      end_date: u.end_date,
      reason: u.reason,
      note: u.note,
      created_at: u.created_at,
    }));
  }

  static async createUnavailability(
    itemId: string,
    payload: {
      start_date: string
      end_date: string
      reason: UnavailabilityReason
      note?: string
    },
  ): Promise<RentalUnavailability> {
    const res = await axiosInstance.post<{ unavailability: CatalogUnavailabilityResponse }>(
      url(YEATI_LOC_ENDPOINTS.unavailabilities.CREATE_FOR_ITEM(itemId)),
      { unavailability: payload },
    );
    const u = res.data.unavailability;
    return {
      id: u.id, start_date: u.start_date, end_date: u.end_date,
      reason: u.reason, note: u.note, created_at: u.created_at,
    };
  }

  static async deleteUnavailability(id: string): Promise<void> {
    await axiosInstance.delete(url(YEATI_LOC_ENDPOINTS.unavailabilities.DELETE(id)));
  }
}
